import { Check, LinesField, NumberField, RefsPicker, Select, TextArea, TextField } from "../fields";
import { OptionsEditor } from "./OptionsEditor";
import type { B, EditorProps } from "./types";

export function ConceptEditor({ value: b, onChange, deck }: EditorProps<B<"concept">>) {
  const set = (patch: Partial<B<"concept">>) => onChange({ ...b, ...patch });
  return (
    <>
      <TextField label="Title" max={80} value={b.title} onChange={(title) => set({ title })} />
      <TextArea label="Lead" max={240} value={b.lead} onChange={(lead) => set({ lead: lead || undefined })} />
      <LinesField
        label="Bullets"
        hint="One per line, up to 4, 120 characters each."
        maxItems={4}
        value={b.bullets}
        onChange={(bullets) => set({ bullets })}
      />
      <TextField
        label="Or one big statement instead"
        hint="Shown instead of the bullets."
        max={140}
        value={b.statement}
        onChange={(statement) => set({ statement: statement || undefined })}
      />
      <RefsPicker references={deck.references} value={b.refs} onChange={(refs) => set({ refs })} />
      <TextArea
        label="Speaker notes"
        max={2000}
        value={b.notes}
        onChange={(notes) => set({ notes: notes || undefined })}
      />
    </>
  );
}

const KINDS = [
  { value: "single", label: "Single choice" },
  { value: "multi", label: "Pick several" },
  { value: "scale", label: "Scale" },
  { value: "wordcloud", label: "Word cloud" },
  { value: "open", label: "Open text" },
] as const;

export function PollEditor({ value: b, onChange }: EditorProps<B<"poll">>) {
  const set = (patch: Partial<B<"poll">>) => onChange({ ...b, ...patch });
  const choice = b.kind === "single" || b.kind === "multi";
  const scale = b.scale ?? { min: 1, max: 5 };
  return (
    <>
      <TextField label="Question" max={140} value={b.question} onChange={(question) => set({ question })} />
      <Select
        label="Kind"
        value={b.kind}
        options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
        onChange={(kind) =>
          set({
            kind,
            ...(kind === "scale" && !b.scale && { scale: { min: 1, max: 5 } }),
            ...((kind === "single" || kind === "multi") &&
              b.options.length < 2 && {
                options: [
                  { id: "a", label: "First option" },
                  { id: "b", label: "Second option" },
                ],
              }),
          })
        }
      />
      {choice && <OptionsEditor value={b.options} onChange={(options) => set({ options })} />}
      {b.kind === "scale" && (
        <div className="grid-2">
          <NumberField label="From" value={scale.min} onChange={(min) => set({ scale: { ...scale, min: min ?? 1 } })} />
          <NumberField label="To" value={scale.max} onChange={(max) => set({ scale: { ...scale, max: max ?? 5 } })} />
          <TextField
            label="Label at the low end"
            max={30}
            value={scale.minLabel}
            onChange={(minLabel) => set({ scale: { ...scale, minLabel: minLabel || undefined } })}
          />
          <TextField
            label="Label at the high end"
            max={30}
            value={scale.maxLabel}
            onChange={(maxLabel) => set({ scale: { ...scale, maxLabel: maxLabel || undefined } })}
          />
        </div>
      )}
      {!choice && b.kind !== "scale" && (
        <p className="empty">Phones get a text box (80 characters). Answers show on screen when you reveal.</p>
      )}
      <TextArea
        label="Speaker notes"
        max={2000}
        value={b.notes}
        onChange={(notes) => set({ notes: notes || undefined })}
      />
    </>
  );
}

export function QuizEditor({ value: b, onChange }: EditorProps<B<"quiz">>) {
  const set = (patch: Partial<B<"quiz">>) => onChange({ ...b, ...patch });
  return (
    <>
      <TextField label="Question" max={140} value={b.question} onChange={(question) => set({ question })} />
      <OptionsEditor
        value={b.options}
        onChange={(options) =>
          set({
            options,
            correctOptionId: options.some((o) => o.id === b.correctOptionId) ? b.correctOptionId : options[0]!.id,
          })
        }
      />
      <Select
        label="Correct answer (hidden until you reveal)"
        value={b.correctOptionId}
        options={b.options.map((o, i) => ({
          value: o.id,
          label: `${String.fromCharCode(65 + i)}. ${o.label || "(empty)"}`,
        }))}
        onChange={(correctOptionId) => set({ correctOptionId })}
      />
      <TextArea
        label="Explanation, shown on reveal"
        max={240}
        value={b.explanation}
        onChange={(explanation) => set({ explanation: explanation || undefined })}
      />
      <TextArea
        label="Speaker notes"
        max={2000}
        value={b.notes}
        onChange={(notes) => set({ notes: notes || undefined })}
      />
    </>
  );
}

export function ReferencesBlockEditor({ value: b, onChange, deck }: EditorProps<B<"references">>) {
  const all = b.referenceIds === undefined;
  return (
    <>
      <TextField label="Title" max={80} value={b.title} onChange={(title) => onChange({ ...b, title })} />
      <Check
        label="Show every reference in the deck"
        checked={all}
        onChange={(on) => onChange({ ...b, referenceIds: on ? undefined : deck.references.map((r) => r.id) })}
      />
      {!all && (
        <RefsPicker
          label="Show these"
          references={deck.references}
          value={b.referenceIds ?? []}
          onChange={(referenceIds) => onChange({ ...b, referenceIds })}
        />
      )}
      <p className="empty">This list is also pushed to phones while it’s on screen, and after the session ends.</p>
    </>
  );
}
